/**
 * Pong — rules and simulation for the Digital Company ping pong table.
 *
 * Coordinates are a 720×420 court. The left and right paddles belong to
 * the two players (or the player and the computer). First to 7, win by 2,
 * with a cap so a long deuce still ends.
 */

export const TABLE_W = 720;
export const TABLE_H = 420;
export const PADDLE_W = 12;
export const PADDLE_H = 78;
export const BALL_R = 8;
export const PADDLE_INSET = 24;
export const WIN_SCORE = 7;
export const WIN_BY = 2;
export const SCORE_CAP = 15;

export type PongSide = 'left' | 'right';
export type PongPhase = 'serve' | 'play' | 'over';

export interface PongState {
  ballX: number;
  ballY: number;
  ballVx: number;
  ballVy: number;
  leftY: number;
  rightY: number;
  scoreL: number;
  scoreR: number;
  phase: PongPhase;
  server: PongSide;
  winner: PongSide | null;
  serveTimer: number;
  rally: number;
}

export interface PongSnapshot {
  seq: number;
  at: number;
  ballX: number;
  ballY: number;
  ballVx: number;
  ballVy: number;
  leftY: number;
  rightY: number;
  scoreL: number;
  scoreR: number;
  phase: PongPhase;
  server: PongSide;
  winner: PongSide | null;
}

export interface PongStepResult {
  hit: boolean;
  scored: PongSide | null;
  over: boolean;
}

export interface PongInput {
  /** Desired paddle-centre Y. Null keeps the current Y (remote / idle). */
  leftTarget: number | null;
  rightTarget: number | null;
  /** Computer tracks the ball on the right. Ignores `rightTarget`. */
  aiRight?: boolean;
}

const PADDLE_SPEED = 540;
const AI_SPEED = 185;

export function createPongState(): PongState {
  return {
    ballX: TABLE_W / 2 - 36,
    ballY: TABLE_H / 2,
    ballVx: 0,
    ballVy: 0,
    leftY: TABLE_H / 2,
    rightY: TABLE_H / 2,
    scoreL: 0,
    scoreR: 0,
    phase: 'serve',
    server: 'left',
    winner: null,
    serveTimer: 0.95,
    rally: 0,
  };
}

export function toSnapshot(state: PongState, seq: number, at = Date.now()): PongSnapshot {
  return {
    seq,
    at,
    ballX: state.ballX,
    ballY: state.ballY,
    ballVx: state.ballVx,
    ballVy: state.ballVy,
    leftY: state.leftY,
    rightY: state.rightY,
    scoreL: state.scoreL,
    scoreR: state.scoreR,
    phase: state.phase,
    server: state.server,
    winner: state.winner,
  };
}

export function isPongSnapshot(v: unknown): v is PongSnapshot {
  if (!v || typeof v !== 'object') return false;
  const s = v as PongSnapshot;
  const nums = [
    s.seq,
    s.at,
    s.ballX,
    s.ballY,
    s.ballVx,
    s.ballVy,
    s.leftY,
    s.rightY,
    s.scoreL,
    s.scoreR,
  ];
  if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) return false;
  if (s.scoreL < 0 || s.scoreR < 0 || s.scoreL > 30 || s.scoreR > 30) return false;
  if (Math.abs(s.ballVx) > 2000 || Math.abs(s.ballVy) > 2000) return false;
  if (s.phase !== 'serve' && s.phase !== 'play' && s.phase !== 'over') return false;
  if (s.server !== 'left' && s.server !== 'right') return false;
  if (s.winner !== null && s.winner !== 'left' && s.winner !== 'right') return false;
  return true;
}

/** Guest-side smoothing. Clocks differ, so the look-ahead stays short. */
export function extrapolateSnap(snap: PongSnapshot, now: number): PongSnapshot {
  if (snap.phase !== 'play') return snap;
  const t = Math.max(0, Math.min(0.1, (now - snap.at) / 1000));
  if (t <= 0) return snap;
  let x = snap.ballX;
  let y = snap.ballY;
  let vy = snap.ballVy;
  const vx = snap.ballVx;
  const steps = 2;
  const dt = t / steps;
  for (let i = 0; i < steps; i++) {
    x += vx * dt;
    y += vy * dt;
    if (y < BALL_R) {
      y = BALL_R;
      vy = Math.abs(vy);
    } else if (y > TABLE_H - BALL_R) {
      y = TABLE_H - BALL_R;
      vy = -Math.abs(vy);
    }
  }
  return { ...snap, ballX: x, ballY: y, ballVy: vy };
}

export function paddleBounds(): { min: number; max: number } {
  const half = PADDLE_H / 2;
  return { min: half + 6, max: TABLE_H - half - 6 };
}

function clampPaddle(y: number): number {
  const { min, max } = paddleBounds();
  return Math.max(min, Math.min(max, y));
}

function moveToward(current: number, target: number, maxStep: number): number {
  const delta = clampPaddle(target) - current;
  if (Math.abs(delta) <= maxStep) return clampPaddle(target);
  return current + Math.sign(delta) * maxStep;
}

function launch(state: PongState, toward: PongSide) {
  const angle = Math.random() * 0.8 - 0.4;
  const speed = 300 + Math.random() * 24;
  const dir = toward === 'right' ? 1 : -1;
  state.ballVx = dir * speed * Math.cos(angle);
  state.ballVy = speed * Math.sin(angle);
  if (Math.abs(state.ballVy) < 45) state.ballVy = state.ballVy < 0 ? -65 : 65;
}

function placeServeBall(state: PongState) {
  state.ballVx = 0;
  state.ballVy = 0;
  const held =
    state.server === 'left'
      ? PADDLE_INSET + PADDLE_W + BALL_R + 3
      : TABLE_W - PADDLE_INSET - PADDLE_W - BALL_R - 3;
  state.ballX = held;
  state.ballY = state.server === 'left' ? state.leftY : state.rightY;
}

function scorePoint(state: PongState, who: PongSide): { over: boolean } {
  if (who === 'left') state.scoreL += 1;
  else state.scoreR += 1;
  state.rally = 0;
  const hi = Math.max(state.scoreL, state.scoreR);
  const diff = Math.abs(state.scoreL - state.scoreR);
  if ((hi >= WIN_SCORE && diff >= WIN_BY) || hi >= SCORE_CAP) {
    state.phase = 'over';
    state.winner = state.scoreL > state.scoreR ? 'left' : 'right';
    state.ballVx = 0;
    state.ballVy = 0;
    state.ballX = TABLE_W / 2;
    state.ballY = TABLE_H / 2;
    return { over: true };
  }
  // The side that was scored on serves the next point.
  state.phase = 'serve';
  state.server = who === 'left' ? 'right' : 'left';
  state.serveTimer = 0.85;
  placeServeBall(state);
  return { over: false };
}

function bouncePaddle(state: PongState, side: PongSide): boolean {
  const paddleX = side === 'left' ? PADDLE_INSET : TABLE_W - PADDLE_INSET - PADDLE_W;
  const paddleY = side === 'left' ? state.leftY : state.rightY;
  const movingToward = side === 'left' ? state.ballVx < 0 : state.ballVx > 0;
  if (!movingToward) return false;
  const overlapX = state.ballX + BALL_R >= paddleX && state.ballX - BALL_R <= paddleX + PADDLE_W;
  const overlapY = Math.abs(state.ballY - paddleY) <= PADDLE_H / 2 + BALL_R * 0.35;
  if (!overlapX || !overlapY) return false;
  const half = PADDLE_H / 2;
  const offset = Math.max(-1, Math.min(1, (state.ballY - paddleY) / half));
  const speed = Math.min(680, Math.hypot(state.ballVx, state.ballVy) * 1.06 + 12);
  const angle = offset * 1.05 + (Math.random() - 0.5) * 0.45;
  const dir = side === 'left' ? 1 : -1;
  state.ballVx = dir * speed * Math.cos(angle);
  state.ballVy = speed * Math.sin(angle);
  state.ballX = side === 'left' ? paddleX + PADDLE_W + BALL_R : paddleX - BALL_R;
  state.rally += 1;
  return true;
}

function stepSlice(state: PongState, dt: number, input: PongInput): PongStepResult {
  const result: PongStepResult = { hit: false, scored: null, over: false };
  if (input.leftTarget != null) {
    state.leftY = moveToward(state.leftY, input.leftTarget, PADDLE_SPEED * dt);
  }
  if (input.aiRight && state.phase !== 'over') {
    // Track late and a bit off-centre so a player who places the ball can win.
    let aim = TABLE_H / 2;
    if (state.phase === 'play' && state.ballVx > 0) {
      aim = state.ballY + state.ballVy * 0.2;
      aim += Math.sin(state.ballX * 0.02 + state.rally * 1.7) * 78;
    }
    state.rightY = moveToward(state.rightY, aim, AI_SPEED * dt);
  } else if (input.rightTarget != null) {
    state.rightY = moveToward(state.rightY, input.rightTarget, PADDLE_SPEED * dt);
  }

  if (state.phase === 'over') return result;

  if (state.phase === 'serve') {
    state.serveTimer -= dt;
    placeServeBall(state);
    if (state.serveTimer <= 0) {
      state.phase = 'play';
      launch(state, state.server === 'left' ? 'right' : 'left');
    }
    return result;
  }

  state.ballX += state.ballVx * dt;
  state.ballY += state.ballVy * dt;

  if (state.ballY < BALL_R) {
    state.ballY = BALL_R;
    state.ballVy = Math.abs(state.ballVy);
  } else if (state.ballY > TABLE_H - BALL_R) {
    state.ballY = TABLE_H - BALL_R;
    state.ballVy = -Math.abs(state.ballVy);
  }

  if (bouncePaddle(state, 'left') || bouncePaddle(state, 'right')) result.hit = true;

  if (state.ballX < -BALL_R) {
    result.scored = 'right';
    result.over = scorePoint(state, 'right').over;
  } else if (state.ballX > TABLE_W + BALL_R) {
    result.scored = 'left';
    result.over = scorePoint(state, 'left').over;
  }
  return result;
}

export function stepPong(state: PongState, dt: number, input: PongInput): PongStepResult {
  const safe = Math.max(0, Math.min(dt, 0.05));
  const slices = Math.max(1, Math.ceil(safe / 0.016));
  const h = safe / slices;
  const result: PongStepResult = { hit: false, scored: null, over: false };
  for (let i = 0; i < slices; i++) {
    const step = stepSlice(state, h, input);
    if (step.hit) result.hit = true;
    if (step.scored) result.scored = step.scored;
    if (step.over) {
      result.over = true;
      break;
    }
  }
  return result;
}
